import localStorageUtil from '$/common/utils/localStorageUtil';
import { getImageExtensionFromUrl } from './index';
import { getCloudWatermarkFormat } from './imageWatermark';

// 七牛云图片处理云函数
const IMAGE_FUNCTION = 'imageView2';
// 视频截帧云处理函数
const VFRAME_FUNCTION = 'vframe/jpg/offset';

/**
 * 检测是否支持webp
 */
export function checkSupportWebp() {
  // 此判断再Safari上有误，safari 14以上支持webp格式（后期可优化）
  const data = document.createElement('canvas').toDataURL('image/webp');
  if (data.indexOf('data:image/webp;') === 0) {
    return true;
  }

  return false;
}
const isServer = typeof window === 'undefined';

/**
 * 获取是否支持 webp（带缓存）
 */
export function getWebPSupportStatus() {
  // 服务端默认支持 webp
  if (isServer) return true;

  try {
    const cached = localStorageUtil.getItem('isSupportWebp');
    if (cached !== null) return cached === 'true';

    const support = checkSupportWebp();
    localStorageUtil.setItem('isSupportWebp', support.toString());
    return support;
  } catch {
    return false;
  }
}

/**
 * 获取图片的webp格式url
 * @param  {String} url   原始URL，七牛云图片地址
 * @returns {string} 返回图片URL的webp格式URL
 */
export function getWebpUrl({ url }: { url: string; }) {
  if ((!/^http/.test(url)) || url.includes('imageView2') || url.includes('imageMogr2')) return url;
  // URL中是否包含了?,!
  const rawUrl = `${url}${url.includes('?') ? '&' : '?'}`;
  if (checkSupportWebp()) {
    return `${rawUrl}imageView2/0/format/webp`;
  }
  return url;
}

/**
 * 图片预加载
 * @param  {Array} urls   图片URL数组
 * @returns {Promise} 返回一个Promise对象，resolve参数为图片URL数组
 */
export function preloadImages(urls: string[]): Promise<{
  width: number;
  height: number;
  src: string;
}[]> {
  if (!urls.length) return Promise.resolve([]);
  const load = (src: string) => new Promise<{
    width: number;
    height: number;
    src: string;
  }>((resolve) => {
    const img = new Image();
    // eslint-disable-next-line no-multi-assign
    img.onload = img.onerror = () => resolve({
      width: img.naturalWidth,
      height: img.naturalHeight,
      src,
    });
    img.src = src;
  });

  return new Promise((resolve) => {
    // 异步调度，不阻塞主逻辑
    setTimeout(() => {
      Promise.all(urls.map(load)).then(resolve);
    }, 0);
  });
}

/**
 * 获取图片 CDN 缩放使用的设备像素比（SSR 下默认 2，与历史行为一致）。
 */
function getImageUrlDevicePixelRatio(): number {
  return typeof window !== 'undefined' ? (window.devicePixelRatio || 2) : 2;
}

/**
 * 将 UI 逻辑尺寸转换为七牛 imageView2 的 w/h 像素参数。
 * @param logicalSize CSS 逻辑像素尺寸
 */
function toCloudImagePixelSize(logicalSize: number): number {
  return Math.ceil(logicalSize * getImageUrlDevicePixelRatio());
}

/**
 * 解析 imageView2 的宽高逻辑尺寸：仅传 size 时只设置宽度并忽略 width、height。
 * @param option 调用方传入的尺寸选项
 */
function resolveCloudImageViewDimensions(option: {
  size?: number;
  width?: number;
  height?: number;
}): { width?: number; height?: number; } {
  const { size, width, height } = option;

  if (size) {
    return { width: size };
  }

  return { width, height };
}

/**
 * 处理图片URL
 * @param url 图片URL
 * @param option.mode 七牛 imageView2 缩放模式，默认 2
 * @param option.size 图片宽度（逻辑像素，会乘以 devicePixelRatio 写入 `/w/`）；传入时忽略 width、height
 * @param option.width 图片宽度（逻辑像素）；与 height 搭配设置 `/w/`、`/h/`，不可与 size 同时使用
 * @param option.height 图片高度（逻辑像素）；与 width 搭配设置 `/h/`
 * @param option.showWatermark 是否显示水印，水印 Base64 可由 InitComponent.watermarkBase64 自定义
 * @returns 处理后的图片URL
 */
export function handleImageUrl(
  url: string,
  option: {
    mode?: 1 | 2 | 3 | 4;
    size?: number;
    width?: number;
    height?: number;
    showWatermark?: boolean;
    needFormat?: boolean;
  } = {},
) {
  const {
    mode = 2, size, width, height, showWatermark, needFormat = true,
  } = option;

  if (!url) return '';

  // 只处理http和https的图片
  if (!/^http[s]?:\/\//.test(url)) return url;

  // 已经有云处理参数（imageView2 或 imageMogr2），则不重复添加
  if (url.includes(IMAGE_FUNCTION) || url.includes('imageMogr2')) return url;

  // svg
  const isSvg = getImageExtensionFromUrl(url).extension === 'svg';

  if (isSvg) return url;

  // gif
  const isGif = getImageExtensionFromUrl(url).extension === 'gif';

  let commonParams = `${IMAGE_FUNCTION}/${mode}`;

  const {
    width: logicalWidth,
    height: logicalHeight,
  } = resolveCloudImageViewDimensions({ size, width, height });

  if (logicalWidth) {
    commonParams += `/w/${toCloudImagePixelSize(logicalWidth)}`;
  }

  if (logicalHeight) {
    commonParams += `/h/${toCloudImagePixelSize(logicalHeight)}`;
  }

  // 如果支持webp，则设置format/webp参数
  if (getWebPSupportStatus() && !isGif && needFormat) {
    commonParams += '/format/webp';
  }

  // 如果需要添加水印，则设置watermark参数
  if (showWatermark) {
    commonParams += `|${getCloudWatermarkFormat()}`;
  }

  // 处理视频截帧封面图
  const urlParams = new URLSearchParams(url.split('?')[1]);
  let vframeFunc = '';
  for (const key of urlParams.keys()) {
    if (key.includes(VFRAME_FUNCTION)) {
      vframeFunc = key;
      const newUrl = url.replace(vframeFunc, `${vframeFunc}|${commonParams}`);
      return newUrl;
    }
  }

  // 设置连接符 &
  const rawUrl = `${url}${url.includes('?') ? '&' : '?'}`;

  return `${rawUrl}${commonParams}`;
}

/**
 * 返回 URL 中不含查询参数（query）、片段标识（hash）及七牛云处理后缀（!）的部分。
 * 即在第一个 `?`、`#` 或 `!` 处截断；无则返回原字符串。
 *
 * @param url 原始 URL 或路径字符串
 * @returns 去掉 query、hash 与云处理后缀后的前缀
 */
export function getUrlWithoutQueryAndHash(url: string): string {
  if (!url) return '';
  return url.split(/[?#!]/)[0];
}

// 根据url获取资源类型
export function getTypebyUrl(url: string) {
  const urlWithoutQuery = getUrlWithoutQueryAndHash(url);

  // 获取最后一个点之后的内容作为后缀名
  const lastDotIndex = urlWithoutQuery.lastIndexOf('.');

  if (lastDotIndex === -1 || lastDotIndex === urlWithoutQuery.length - 1) {
    return ''; // 没有后缀名
  }

  return urlWithoutQuery.substring(lastDotIndex + 1).toLowerCase();
}

// 资源转markdown
export function mediaToMarkdown(media: { name?: string; url: string; }[]) {
  return media.map((item) => `![${item.name || 'image'}](${item.url})`).join('\n');
}
